import * as Sentry from "@sentry/react";
import { isJsonValue, type JsonValue } from "@types";
import { type AxiosError, isAxiosError } from "axios";
import {
  isBigIntValue,
  isBooleanValue,
  isFunctionValue,
  isNumberValue,
  isStringValue,
  isSymbolValue,
} from "@/utils/typePredicates";
import { SAFE_CONSOLE_OPERATIONS } from "./safeConsoleOperations";

export const SAFE_CONSOLE_MARKER = "[sameday-safe]";
export const SAFE_CONSOLE_REDACTED = "[REDACTED]";

export type SafeConsoleOperation = keyof typeof SAFE_CONSOLE_OPERATIONS;
export type SafeLogValue =
  | boolean
  | null
  | number
  | string
  | SafeLogValue[]
  | { [key: string]: SafeLogValue };
type UntrustedLogValue =
  | bigint
  | boolean
  | null
  | number
  | string
  | symbol
  | undefined
  | UntrustedLogFunction
  | UntrustedLogObject;
type UntrustedLogFunction = (...arguments_: never[]) => UntrustedLogValue;
interface UntrustedLogObject {
  readonly [key: string]: UntrustedLogValue;
}
interface UntrustedErrorDetails {
  message: string;
  name: string;
  stack?: string;
  cause?: unknown;
}
type SafeLogScalar = boolean | null | number | string;
type SafeLogRecord = { [key: string]: SafeLogValue };
type SensitiveKeyPredicate = (key: string) => boolean;

interface SafeConsolePayload {
  data: SafeLogValue;
  truncated: boolean;
}

/** One memoized subtree: its sanitized value and the characters it emits. */
interface MemoEntry {
  size: number;
  value: SafeLogValue;
}

/**
 * One sanitization operation. `ancestors` detects true cycles; `memo` makes a
 * shared subgraph cost one traversal instead of one per incoming edge; `nodes`
 * and `output` are the global budgets that terminate hostile inputs.
 */
interface SanitizationState {
  ancestors: Set<object>;
  memo: WeakMap<object, Map<string, MemoEntry>>;
  nodes: number;
  output: number;
  truncated: boolean;
}

/**
 * A sanitized value plus the bookkeeping its parent needs: `size` is the
 * characters it emits (charged again on every memo reuse, because the returned
 * graph is a DAG that expands when serialized) and `cyclic` marks a subtree
 * containing a `[Circular]` marker, which is only valid on the current path and
 * therefore must not be memoized.
 */
interface SanitizedNode<T extends SafeLogValue = SafeLogValue> {
  cyclic: boolean;
  size: number;
  value: T;
}

interface RecordAccounting {
  cyclic: boolean;
  size: number;
}

interface SafeApiFailure extends SafeLogRecord {
  code: string;
  errorName: string;
  method: string;
  outcome: "http" | "network";
  path: string;
  request: SafeLogRecord;
  status: number | null;
}

const MAX_ARRAY_ITEMS = 50;
const MAX_DEPTH = 8;
const MAX_HTTP_METHOD_LENGTH = 16;
const MAX_OBJECT_KEY_LENGTH = 256;
const MAX_OBJECT_KEYS = 50;
const MAX_PATH_SEGMENTS = 64;
const MAX_NESTED_URL_DEPTH = 3;
const MAX_PERCENT_DECODE_PASSES = 3;
const MAX_STRING_LENGTH = 4_096;
const MAX_STRING_SANITIZATION_LENGTH = 65_536;
const MAX_URL_LENGTH = 8_192;
const MAX_LOGGABLE_BIGINT = 10n ** BigInt(MAX_STRING_LENGTH);
// Global per-operation budgets. The per-object limits above are local, so a
// wide, deep, or shared graph can multiply them without ever tripping one;
// these bound total work and total emitted size for the whole operation.
const MAX_TOTAL_NODES = 10_000;
const MAX_TOTAL_OUTPUT_CHARS = 131_072;
const SAFE_PARSE_ORIGIN = "https://safe.invalid";
const UNKNOWN_PATH = "<unknown path>";
const CIRCULAR_VALUE = "[Circular]";
const TRUNCATED_VALUE = "[Truncated]";
const UNAVAILABLE_VALUE = "[Unavailable]";
const SAFE_HTTP_METHODS = new Set([
  "DELETE",
  "GET",
  "HEAD",
  "OPTIONS",
  "PATCH",
  "POST",
  "PUT",
]);

// All key checks run against lowercase alphanumeric names, so camelCase,
// snake_case, kebab-case, and header casing share one policy.
const SAFE_SECRET_METADATA_KEY_PATTERN =
  /(?:checkedat|configured|enabled|expiresat|expiration|present|status|type)$/;
const SENSITIVE_EXACT_KEY_PATTERN =
  /^(?:auth|authorization|backupcode|codechallenge|codeverifier|cookie|credential|credentials|mfacode|oobcode|otp|passphrase|passwd|password|pin|proxyauthorization|recoverycode|secret|securityanswer|setcookie|signature|token|verificationcode)$/;
const SENSITIVE_PREFIX_KEY_PATTERN =
  /^(?:accesstoken|apikey|apisecret|authkey|authtoken|authorization|clientsecret|cookie|idtoken|passphrase|passwd|password|privatekey|refreshtoken|secret|sessiontoken|signingsecret)/;
const SENSITIVE_SUFFIX_KEY_PATTERN =
  /(?:accesstoken|apikey|apisecret|authkey|authtoken|backupcode|codechallenge|codeverifier|cookie|credential|idtoken|mfacode|oobcode|otp|passphrase|passwd|password|pin|privatekey|recoverycode|refreshtoken|secret|securityanswer|sessiontoken|signature|token|tokenpreview|verificationcode)$/;
// Compound credential names whose credential root sits mid-identifier, so
// neither the prefix nor the suffix pattern sees it — `chatWidgetSecretKey`,
// `bookingWizardSecretKey`, `analysisWebhookSigningSecretPlaintext`. Members
// are explicit compounds on purpose: a bare `secret`, `token`, or `key`
// substring rule would redact `secretaryEmail`, `tokensUsed`, and
// `idempotencyKey`.
const SENSITIVE_COMPOUND_KEY_PATTERN =
  /(?:accesskey|accesstoken|apikey|apisecret|apitoken|authkey|authtoken|bearertoken|clientsecret|consumersecret|encryptionkey|hmackey|idtoken|passphrase|passwd|password|privatekey|recoverycode|refreshtoken|secretaccesskey|secretkey|secretplaintext|secrettoken|secretvalue|securitytoken|sessiontoken|sharedsecret|signingkey|signingsecret|webhooksecret)/;
const SENSITIVE_QUERY_KEY_PATTERN =
  /^(?:awsaccesskeyid|code|key|sig|state|t|xamzcredential|xamzsecuritytoken|xamzsignature)$/;
const DIRECT_IDENTIFIER_QUERY_KEY_PATTERN =
  /(?:email|emailaddress|phone|phonenumber)$/;
const CREDENTIAL_PATH_VALUE_PARENT_PATTERN =
  /^(?:api-keys?|password-reset|private-key|refresh-token|reset-password|signing-secret|tokens?)$/;

const AUTHORIZATION_VALUE_PATTERN =
  /\b(?:Basic|Bearer)\s+[A-Za-z0-9._~+/=-]+/gi;
const CONNECTION_STRING_PATTERN =
  /\b(?:amqp|mongodb(?:\+srv)?|mysql|postgres(?:ql)?|redis):\/\/[^\s"'<>]+/gi;
// Scan only field names whose value can begin inside the emitted string.
// Classification is delegated to the same key policy used for structured
// objects, so string and object redaction cannot drift. Percent triplets are
// included so encoded credential names cannot evade the shared policy.
const CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN = new RegExp(
  String.raw`(^|[^A-Za-z0-9_%-])["']?([A-Za-z0-9_%-]{1,${MAX_STRING_LENGTH}})["']?\]?(?:\[\])?\s*[:=]\s*`,
  "gi",
);
const CREDENTIAL_PREFIX_PATTERN =
  /\b(?:AKIA[0-9A-Z]{16}|ASIA[0-9A-Z]{16}|AIza[A-Za-z0-9_-]{30,}|SG\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|github_pat_[A-Za-z0-9_]{20,}|gh[pousr]_[A-Za-z0-9]{20,}|glpat-[A-Za-z0-9_-]{20,}|npm_[A-Za-z0-9]{20,}|[rs]k_(?:live|test)_[A-Za-z0-9]{16,}|sk-(?:proj-)?[A-Za-z0-9_-]{16,}|sq0(?:atp|csp)-[A-Za-z0-9_-]{16,}|whsec_[A-Za-z0-9]{16,}|xox[baprs]-[A-Za-z0-9-]{10,})\b/g;
const EMAIL_PATH_VALUE_PATTERN = /^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/;
const E164_PHONE_PATH_VALUE_PATTERN = /^\+[1-9][0-9]{6,14}$/;
const ABSOLUTE_HTTP_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const RELATIVE_DIAGNOSTIC_URL_PATTERN = /(^|[\s(])((?:\/|\?)[^\s"'<>]+)/gi;
const JWT_PATTERN =
  /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g;
const PRIVATE_KEY_PATTERN =
  /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY-----[\s\S]*?(?:-----END(?: [A-Z0-9]+)* PRIVATE KEY-----|$)/g;
const SECRET_PATH_VALUE_PATTERN =
  /^(?:eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}|AKIA[0-9A-Z]{16}|ASIA[0-9A-Z]{16}|AIza[A-Za-z0-9_-]{30,}|SG\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|SK[a-fA-F0-9]{32}|github_pat_[A-Za-z0-9_]{20,}|gh[pousr]_[A-Za-z0-9]{20,}|glpat-[A-Za-z0-9_-]{20,}|npm_[A-Za-z0-9]{20,}|[rs]k_(?:live|test)_[A-Za-z0-9]{16,}|sk-(?:proj-)?[A-Za-z0-9_-]{16,}|sq0(?:atp|csp)-[A-Za-z0-9_-]{16,}|whsec_[A-Za-z0-9]{16,}|xox[baprs]-[A-Za-z0-9-]{10,})$/;
const SAFE_ERROR_CODE_PATTERN = /^[A-Z0-9_]{1,64}$/;
const PERCENT_ENCODED_BYTE_PATTERN = /%[A-Fa-f0-9]{2}/;

const createSanitizationState = (): SanitizationState => ({
  ancestors: new Set(),
  memo: new WeakMap(),
  nodes: 0,
  output: 0,
  truncated: false,
});

const isUntrustedLogObject = (value: unknown): value is UntrustedLogObject =>
  typeof value === "object" && value !== null;

const charge = (state: SanitizationState, characters: number): void => {
  state.output += characters;
  if (state.output >= MAX_TOTAL_OUTPUT_CHARS) state.truncated = true;
};

const exhaustOutputBudget = (state: SanitizationState): void => {
  state.output = MAX_TOTAL_OUTPUT_CHARS;
  state.truncated = true;
};

const isBudgetExhausted = (state: SanitizationState): boolean =>
  state.nodes >= MAX_TOTAL_NODES || state.output >= MAX_TOTAL_OUTPUT_CHARS;

const serializedScalarSize = (value: SafeLogScalar): number =>
  JSON.stringify(value).length;

// Includes the serialized key, colon, and one conservative comma. The record's
// initial two characters account for its braces, so this overcounts by one.
const serializedRecordKeySize = (key: string): number =>
  JSON.stringify(key).length + 2;

const createRecordAccounting = (state: SanitizationState): RecordAccounting => {
  charge(state, 2);
  return { cyclic: false, size: 2 };
};

const accountRecordField = (
  key: string,
  node: SanitizedNode,
  state: SanitizationState,
  accounting: RecordAccounting,
): void => {
  const keySize = serializedRecordKeySize(key);
  charge(state, keySize);
  accounting.size += keySize + node.size;
  accounting.cyclic ||= node.cyclic;
};

const appendTruncatedRecordMarker = (
  output: SafeLogRecord,
  state: SanitizationState,
): number => {
  state.truncated = true;
  const keySize = serializedRecordKeySize(TRUNCATED_VALUE);
  charge(state, keySize);
  const marker = truncatedNode(state);
  output[TRUNCATED_VALUE] = marker.value;
  return keySize + marker.size;
};

const setRecordField = (
  output: SafeLogRecord,
  key: string,
  value: SafeLogValue,
): void => {
  // Assignment to an attacker-controlled `__proto__` key invokes Object's
  // legacy setter. Defining an own data property preserves the evidence without
  // mutating the sanitized record's prototype.
  Object.defineProperty(output, key, {
    configurable: true,
    enumerable: true,
    value,
    writable: true,
  });
};

const scalarNode = <T extends SafeLogScalar>(
  value: T,
  state: SanitizationState,
): SanitizedNode<T> => {
  const size = serializedScalarSize(value);
  charge(state, size);
  return { cyclic: false, size, value };
};

const truncatedNode = (state: SanitizationState): SanitizedNode<string> => {
  state.truncated = true;
  return scalarNode(TRUNCATED_VALUE, state);
};

const tryDecodeRepeatedly = (value: string): string | null => {
  let decoded = value;
  for (let pass = 0; pass < MAX_PERCENT_DECODE_PASSES; pass += 1) {
    if (!PERCENT_ENCODED_BYTE_PATTERN.test(decoded)) return decoded;
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) return decoded;
      decoded = next;
    } catch {
      return null;
    }
  }
  return PERCENT_ENCODED_BYTE_PATTERN.test(decoded) ? null : decoded;
};

const normalizeKey = (key: string): string =>
  // Partially valid or over-nested encoding is treated as credential-shaped:
  // diagnostic evidence is less important than preventing an encoded bypass.
  (tryDecodeRepeatedly(key) ?? "credential")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isSensitiveKey = (key: string): boolean => {
  const normalized = normalizeKey(key);
  if (SAFE_SECRET_METADATA_KEY_PATTERN.test(normalized)) return false;
  return (
    SENSITIVE_EXACT_KEY_PATTERN.test(normalized) ||
    SENSITIVE_PREFIX_KEY_PATTERN.test(normalized) ||
    SENSITIVE_SUFFIX_KEY_PATTERN.test(normalized) ||
    SENSITIVE_COMPOUND_KEY_PATTERN.test(normalized)
  );
};

interface StringReplacementRange {
  end: number;
  start: number;
}

const isUnquotedCredentialTerminator = (value: string): boolean =>
  value === '"' ||
  value === "'" ||
  value === "," ||
  value === "}" ||
  value === "&" ||
  value === ";" ||
  value === "\r" ||
  value === "\n";

const isCredentialQuoteBoundary = (value: string): boolean =>
  value === "" ||
  /\s/.test(value) ||
  value === "," ||
  value === "." ||
  value === ":" ||
  value === ";" ||
  value === "!" ||
  value === "?" ||
  value === "&" ||
  value === ")" ||
  value === "]" ||
  value === "}";

const findClosingCredentialQuote = (
  value: string,
  start: number,
  quote: string,
): number => {
  for (let index = start; index < value.length; index += 1) {
    if (value[index] === "\\") {
      index += 1;
      continue;
    }
    if (
      value[index] === quote &&
      isCredentialQuoteBoundary(value[index + 1] ?? "")
    ) {
      return index;
    }
  }
  return -1;
};

/**
 * Find credential assignment value ranges in one linear pass. Field names are
 * bounded and classified by `isSensitiveKey`; quoted values retain their quote
 * characters, while unquoted values stop at the same diagnostic delimiters as
 * the previous assignment regexes.
 */
const getCredentialReplacementRanges = (
  value: string,
): StringReplacementRange[] => {
  const ranges: StringReplacementRange[] = [];
  CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex = 0;
  let match = CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.exec(value);
  while (match) {
    const fieldName = match[2] ?? "";
    const valueStart = CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex;
    if (!isSensitiveKey(fieldName)) {
      // Revisit the delimiter immediately before this non-sensitive value so a
      // credential assignment at the value's start is still discoverable.
      CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex = Math.max(
        match.index + 1,
        valueStart - 1,
      );
      match = CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.exec(value);
      continue;
    }

    const quote = value[valueStart];
    if (quote === '"' || quote === "'") {
      const start = valueStart + 1;
      const closingQuote = findClosingCredentialQuote(value, start, quote);
      const end = closingQuote < 0 ? value.length : closingQuote;
      ranges.push({ end, start });
      CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex =
        closingQuote < 0 ? value.length : closingQuote + 1;
    } else {
      let end = valueStart;
      while (
        end < value.length &&
        !isUnquotedCredentialTerminator(value[end] ?? "")
      ) {
        end += 1;
      }
      if (end > valueStart) ranges.push({ end, start: valueStart });
      CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex = end;
    }
    match = CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.exec(value);
  }
  CREDENTIAL_FIELD_ASSIGNMENT_PREFIX_PATTERN.lastIndex = 0;
  return ranges;
};

const redactCredentialAssignments = (value: string): string => {
  const ranges = getCredentialReplacementRanges(value);
  if (ranges.length === 0) return value;

  let cursor = 0;
  let output = "";
  for (const range of ranges) {
    output += `${value.slice(cursor, range.start)}${SAFE_CONSOLE_REDACTED}`;
    cursor = range.end;
  }
  return `${output}${value.slice(cursor)}`;
};

const hasCredentialFieldAssignment = (value: string): boolean =>
  getCredentialReplacementRanges(value).length > 0;

const matchesSensitiveStringPattern = (
  pattern: RegExp,
  value: string,
): boolean => {
  pattern.lastIndex = 0;
  const matches = pattern.test(value);
  pattern.lastIndex = 0;
  return matches;
};

const hasHttpUrlCredentials = (value: string): boolean => {
  ABSOLUTE_HTTP_URL_PATTERN.lastIndex = 0;
  let match = ABSOLUTE_HTTP_URL_PATTERN.exec(value);
  while (match) {
    try {
      const parsed = new URL(match[0]);
      if (parsed.username.length > 0 || parsed.password.length > 0) {
        ABSOLUTE_HTTP_URL_PATTERN.lastIndex = 0;
        return true;
      }
    } catch {
      // Continue scanning; malformed candidates are handled by URL sanitation.
    }
    match = ABSOLUTE_HTTP_URL_PATTERN.exec(value);
  }
  ABSOLUTE_HTTP_URL_PATTERN.lastIndex = 0;
  return false;
};

const hasEncodedSensitiveValue = (value: string): boolean => {
  if (!PERCENT_ENCODED_BYTE_PATTERN.test(value)) return false;
  const decoded = tryDecodeRepeatedly(value);
  // Ambiguous or over-nested encodings are redacted conservatively; otherwise
  // redact only a sensitive representation exposed by decoding. An assignment
  // whose value is already `[REDACTED]` is stable under the replacement pass,
  // so encoded field names retain their surrounding diagnostic evidence.
  return (
    decoded === null ||
    (decoded !== value &&
      (redactCredentialAssignments(decoded) !== decoded ||
        matchesSensitiveStringPattern(PRIVATE_KEY_PATTERN, decoded) ||
        matchesSensitiveStringPattern(CONNECTION_STRING_PATTERN, decoded) ||
        matchesSensitiveStringPattern(AUTHORIZATION_VALUE_PATTERN, decoded) ||
        matchesSensitiveStringPattern(JWT_PATTERN, decoded) ||
        matchesSensitiveStringPattern(CREDENTIAL_PREFIX_PATTERN, decoded) ||
        hasHttpUrlCredentials(decoded)))
  );
};

const isSensitiveQueryKey = (key: string): boolean => {
  const normalized = normalizeKey(key);
  return (
    SENSITIVE_QUERY_KEY_PATTERN.test(normalized) ||
    DIRECT_IDENTIFIER_QUERY_KEY_PATTERN.test(normalized) ||
    isSensitiveKey(key)
  );
};

const tryDecodePathSegment = (segment: string): string | null =>
  tryDecodeRepeatedly(segment);

const normalizedPathSegment = (segments: string[], index: number): string =>
  tryDecodePathSegment(segments[index] ?? "")?.toLowerCase() ?? "";

const isPhoneNumberPathPosition = (
  segments: string[],
  index: number,
): boolean => {
  const parent = normalizedPathSegment(segments, index - 1);
  if (parent === "phone-numbers") {
    const value = normalizedPathSegment(segments, index);
    return value !== "available" && value !== "search";
  }
  const grandparent = normalizedPathSegment(segments, index - 2);
  return (
    grandparent === "phone" && (parent === "numbers" || parent === "routes")
  );
};

const isTerminalIntegrationCustomerValue = (
  segments: string[],
  index: number,
): boolean =>
  index >= 3 &&
  normalizedPathSegment(segments, index - 1) === "customers" &&
  normalizedPathSegment(segments, index - 3) === "integrations" &&
  !segments.slice(index + 1).some(Boolean);

const sanitizePath = (pathname: string, state: SanitizationState): string => {
  const segments = pathname.split("/");
  if (segments.length > MAX_PATH_SEGMENTS) {
    state.truncated = true;
    return UNKNOWN_PATH;
  }

  let redactNext = false;
  const sanitized = segments.map((segment, index) => {
    if (!segment) return segment;
    const decoded = tryDecodePathSegment(segment);
    if (decoded === null) return SAFE_CONSOLE_REDACTED;

    if (redactNext) {
      redactNext = false;
      return SAFE_CONSOLE_REDACTED;
    }

    const normalized = decoded.toLowerCase();
    if (CREDENTIAL_PATH_VALUE_PARENT_PATTERN.test(normalized)) {
      redactNext = true;
      return segment;
    }

    if (
      SECRET_PATH_VALUE_PATTERN.test(decoded) ||
      E164_PHONE_PATH_VALUE_PATTERN.test(decoded) ||
      EMAIL_PATH_VALUE_PATTERN.test(decoded) ||
      hasCredentialFieldAssignment(decoded) ||
      isPhoneNumberPathPosition(segments, index) ||
      isTerminalIntegrationCustomerValue(segments, index)
    ) {
      return SAFE_CONSOLE_REDACTED;
    }
    return segment;
  });
  return sanitized.join("/") || "/";
};

const trimSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, "");

const parseHttpUrl = (
  requestUrl: UntrustedLogValue,
  baseUrl: UntrustedLogValue,
  state: SanitizationState,
): URL | null => {
  let request = requestUrl;
  let base = baseUrl;
  if (request === undefined || request === null || request === "") {
    request = base;
    base = undefined;
  }
  if (!isStringValue(request) || request.length > MAX_URL_LENGTH) {
    if (isStringValue(request)) state.truncated = true;
    return null;
  }

  try {
    let parsed: URL;
    if (/^[a-z][a-z0-9+.-]*:/i.test(request)) {
      parsed = new URL(request);
    } else if (request.startsWith("//")) {
      parsed = new URL(`https:${request}`);
    } else {
      const parsedBase =
        isStringValue(base) && base.length <= MAX_URL_LENGTH
          ? new URL(base, SAFE_PARSE_ORIGIN)
          : new URL(SAFE_PARSE_ORIGIN);
      if (!["http:", "https:"].includes(parsedBase.protocol)) return null;
      const combined =
        request.startsWith("?") || request.startsWith("#")
          ? `${parsedBase.pathname}${request}`
          : `/${[trimSlashes(parsedBase.pathname), trimSlashes(request)]
              .filter(Boolean)
              .join("/")}`;
      parsed = new URL(combined, parsedBase.origin);
    }
    return ["http:", "https:"].includes(parsed.protocol) ? parsed : null;
  } catch {
    return null;
  }
};

const sanitizeUrl = (
  requestUrl: UntrustedLogValue,
  baseUrl: UntrustedLogValue,
  state: SanitizationState,
  nestedDepth = 0,
): string => {
  const parsed = parseHttpUrl(requestUrl, baseUrl, state);
  if (!parsed) return UNKNOWN_PATH;

  const pathname = sanitizePath(parsed.pathname, state);
  if (pathname === UNKNOWN_PATH) return UNKNOWN_PATH;

  const query: string[] = [];
  for (const [key, value] of parsed.searchParams) {
    if (isBudgetExhausted(state)) {
      state.truncated = true;
      break;
    }
    // Charge every parameter, not just the ones whose value is sanitized: a
    // query of only redacted keys would otherwise loop unbounded.
    charge(state, key.length + 2);
    let safeValue: string;
    if (isSensitiveQueryKey(key)) {
      safeValue = SAFE_CONSOLE_REDACTED;
    } else if (/^(?:https?:\/\/|\/\/|\/|\?)/i.test(value)) {
      if (nestedDepth >= MAX_NESTED_URL_DEPTH) {
        state.truncated = true;
        safeValue = SAFE_CONSOLE_REDACTED;
      } else {
        const nestedUrl = sanitizeUrl(value, undefined, state, nestedDepth + 1);
        safeValue = encodeURIComponent(
          nestedUrl === UNKNOWN_PATH ? SAFE_CONSOLE_REDACTED : nestedUrl,
        );
      }
    } else {
      safeValue = encodeURIComponent(sanitizeString(value, state));
    }
    query.push(`${encodeURIComponent(key)}=${safeValue}`);
  }
  const result = `${pathname}${query.length > 0 ? `?${query.join("&")}` : ""}`;
  if (result.length > MAX_URL_LENGTH) {
    state.truncated = true;
    return UNKNOWN_PATH;
  }
  return result;
};

const sanitizeString = (value: string, state: SanitizationState): string => {
  if (isBudgetExhausted(state)) {
    state.truncated = true;
    return TRUNCATED_VALUE;
  }
  const wasTruncated = value.length > MAX_STRING_LENGTH;
  if (wasTruncated) state.truncated = true;
  // Charge the scanned length before running the pattern chain so the global
  // budget bounds total regex work, not just total emitted characters.
  const scanned = Math.min(value.length, MAX_STRING_SANITIZATION_LENGTH);
  if (state.output + scanned >= MAX_TOTAL_OUTPUT_CHARS) {
    exhaustOutputBudget(state);
    return TRUNCATED_VALUE;
  }
  charge(state, scanned);
  if (value.length > MAX_STRING_SANITIZATION_LENGTH) return TRUNCATED_VALUE;
  let sanitized = value;

  sanitized = sanitized
    .replace(PRIVATE_KEY_PATTERN, SAFE_CONSOLE_REDACTED)
    .replace(CONNECTION_STRING_PATTERN, SAFE_CONSOLE_REDACTED)
    .replace(AUTHORIZATION_VALUE_PATTERN, SAFE_CONSOLE_REDACTED)
    .replace(JWT_PATTERN, SAFE_CONSOLE_REDACTED)
    .replace(CREDENTIAL_PREFIX_PATTERN, SAFE_CONSOLE_REDACTED);
  sanitized = redactCredentialAssignments(sanitized);
  if (hasEncodedSensitiveValue(sanitized)) {
    sanitized = SAFE_CONSOLE_REDACTED;
  }
  sanitized = sanitized.replace(
    RELATIVE_DIAGNOSTIC_URL_PATTERN,
    (_match, prefix: string, candidate: string) => {
      const path = sanitizeUrl(candidate, undefined, state);
      return `${prefix}${path === UNKNOWN_PATH ? SAFE_CONSOLE_REDACTED : path}`;
    },
  );
  sanitized = sanitized.replace(ABSOLUTE_HTTP_URL_PATTERN, (candidate) => {
    const path = sanitizeUrl(candidate, undefined, state);
    return path === UNKNOWN_PATH ? SAFE_CONSOLE_REDACTED : path;
  });

  const result = `${sanitized.slice(0, MAX_STRING_LENGTH)}${wasTruncated ? TRUNCATED_VALUE : ""}`;
  const serializedSize = serializedScalarSize(result);
  if (serializedSize > scanned) {
    const expansion = serializedSize - scanned;
    if (state.output + expansion >= MAX_TOTAL_OUTPUT_CHARS) {
      exhaustOutputBudget(state);
      return TRUNCATED_VALUE;
    }
    charge(state, expansion);
  }
  return result;
};

/** `sanitizeString` as a node; the string itself is charged by that call. */
const sanitizeStringNode = (
  value: string,
  state: SanitizationState,
): SanitizedNode<string> => {
  const sanitized = sanitizeString(value, state);
  return {
    cyclic: false,
    size: serializedScalarSize(sanitized),
    value: sanitized,
  };
};

const getSafeErrorCodeNode = (
  code: string | undefined,
  state: SanitizationState,
): SanitizedNode<string> => {
  const node = sanitizeStringNode(code ?? "unknown", state);
  if (SAFE_ERROR_CODE_PATTERN.test(node.value)) return node;

  const value = "unknown";
  const size = serializedScalarSize(value);
  if (size > node.size) charge(state, size - node.size);
  return { cyclic: false, size, value };
};

const getSafeHttpMethod = (method: string | undefined): string => {
  if (!method || method.length > MAX_HTTP_METHOD_LENGTH) return "UNKNOWN";
  const normalized = method.toUpperCase();
  return SAFE_HTTP_METHODS.has(normalized) ? normalized : "UNKNOWN";
};

const tryParseJson = (value: string): JsonValue | undefined => {
  const trimmed = value.trim();
  if (
    !(
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    )
  ) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isJsonValue(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Memo identity for one object: its result depends on the depth it was reached
 * at (which bounds its descendants) and on the key policy applied to it.
 */
const memoKeyFor = (
  sensitiveKeyPredicate: SensitiveKeyPredicate,
  depth: number,
): string =>
  `${sensitiveKeyPredicate === isSensitiveQueryKey ? "query" : "body"}:${depth}`;

const expandObject = (
  value: UntrustedLogObject,
  state: SanitizationState,
  depth: number,
  sensitiveKeyPredicate: SensitiveKeyPredicate,
): SanitizedNode => {
  if (!isUntrustedLogObject(value)) {
    return scalarNode(UNAVAILABLE_VALUE, state);
  }
  if (isAxiosError(value))
    return buildSafeAxiosDiagnostics(value, state, depth);
  if (value instanceof Error) {
    const errorDetails: UntrustedErrorDetails = {
      message: value.message,
      name: value.name,
    };
    if (value.stack) errorDetails.stack = value.stack;
    if ("cause" in value && value.cause !== undefined) {
      errorDetails.cause = value.cause;
    }
    return sanitizeValue(errorDetails, state, depth + 1, sensitiveKeyPredicate);
  }
  if (value instanceof Date) {
    const timestamp = Date.prototype.getTime.call(value);
    return scalarNode(
      Number.isNaN(timestamp)
        ? "Invalid Date"
        : Date.prototype.toISOString.call(value),
      state,
    );
  }
  if (globalThis.URL !== undefined && value instanceof URL) {
    return scalarNode(sanitizeUrl(value.href, undefined, state), state);
  }
  if (
    globalThis.URLSearchParams !== undefined &&
    value instanceof URLSearchParams
  )
    return scalarNode("[URLSearchParams]", state);
  if (globalThis.FormData !== undefined && value instanceof FormData)
    return scalarNode("[FormData]", state);
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_ITEMS) state.truncated = true;
    const items: SafeLogValue[] = [];
    let size = 2;
    let cyclic = false;
    charge(state, 2);
    for (const item of value.slice(0, MAX_ARRAY_ITEMS)) {
      // One conservative comma per item; an array with items overcounts by one.
      charge(state, 1);
      const child = sanitizeValue(
        item,
        state,
        depth + 1,
        sensitiveKeyPredicate,
      );
      items.push(child.value);
      size += child.size + 1;
      cyclic ||= child.cyclic;
    }
    return { cyclic, size, value: items };
  }
  if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
    return scalarNode(`[Binary ${value.byteLength} bytes]`, state);
  }

  const keys = Object.keys(value);
  if (keys.length > MAX_OBJECT_KEYS) state.truncated = true;
  const output: SafeLogRecord = {};
  let size = 2;
  let cyclic = false;
  charge(state, 2);
  for (const key of keys.slice(0, MAX_OBJECT_KEYS)) {
    if (key.length > MAX_OBJECT_KEY_LENGTH) {
      size += appendTruncatedRecordMarker(output, state);
      break;
    }
    const keySize = serializedRecordKeySize(key);
    if (state.output + keySize >= MAX_TOTAL_OUTPUT_CHARS) {
      size += appendTruncatedRecordMarker(output, state);
      break;
    }
    charge(state, keySize);
    size += keySize;
    try {
      const candidate = value[key];
      if (sensitiveKeyPredicate(key)) {
        const redacted = scalarNode(SAFE_CONSOLE_REDACTED, state);
        setRecordField(output, key, redacted.value);
        size += redacted.size;
        continue;
      }
      const child = sanitizeValue(
        candidate,
        state,
        depth + 1,
        sensitiveKeyPredicate,
      );
      setRecordField(output, key, child.value);
      size += child.size;
      cyclic ||= child.cyclic;
    } catch {
      const unavailable = scalarNode(UNAVAILABLE_VALUE, state);
      setRecordField(output, key, unavailable.value);
      size += unavailable.size;
    }
  }
  return { cyclic, size, value: output };
};

/**
 * Sanitize any value. Each object is expanded exactly once per (depth, key
 * policy) and reused at every other edge that reaches it, so a shared subgraph
 * costs one traversal instead of one per incoming edge — linear in distinct
 * nodes rather than exponential in edges, and without losing the evidence a
 * `[Repeated]` marker would drop. What a shared graph does still cost is
 * emitted size, which the global budget bounds.
 */
const sanitizeValue = (
  value: unknown,
  state: SanitizationState,
  depth = 0,
  sensitiveKeyPredicate: SensitiveKeyPredicate = isSensitiveKey,
): SanitizedNode => {
  if (isBudgetExhausted(state)) return truncatedNode(state);
  if (value === null) return scalarNode(null, state);
  if (isStringValue(value)) {
    const parsed =
      value.length <= MAX_STRING_SANITIZATION_LENGTH
        ? tryParseJson(value)
        : undefined;
    return parsed === undefined
      ? sanitizeStringNode(value, state)
      : sanitizeValue(parsed, state, depth, sensitiveKeyPredicate);
  }
  if (isBooleanValue(value)) return scalarNode(value, state);
  if (isNumberValue(value))
    return scalarNode(Number.isFinite(value) ? value : String(value), state);
  if (isBigIntValue(value)) {
    if (value >= MAX_LOGGABLE_BIGINT || value <= -MAX_LOGGABLE_BIGINT) {
      return truncatedNode(state);
    }
    return sanitizeStringNode(value.toString(), state);
  }
  if (value === undefined) return scalarNode("[undefined]", state);
  if (isSymbolValue(value)) return scalarNode("[Symbol]", state);
  if (isFunctionValue(value)) return scalarNode("[Function]", state);
  if (depth >= MAX_DEPTH) return truncatedNode(state);
  if (!isUntrustedLogObject(value)) {
    return scalarNode(UNAVAILABLE_VALUE, state);
  }
  if (state.ancestors.has(value)) {
    return { ...scalarNode(CIRCULAR_VALUE, state), cyclic: true };
  }

  const memoKey = memoKeyFor(sensitiveKeyPredicate, depth);
  const cached = state.memo.get(value)?.get(memoKey);
  if (cached) {
    // The reused subtree is emitted again wherever it appears, so its size is
    // charged again — a shared graph is cheap to traverse but not free to log,
    // and one that no longer fits the remaining budget is truncated whole.
    if (state.output + cached.size > MAX_TOTAL_OUTPUT_CHARS) {
      return truncatedNode(state);
    }
    charge(state, cached.size);
    return { cyclic: false, size: cached.size, value: cached.value };
  }

  state.nodes += 1;
  state.ancestors.add(value);
  let node: SanitizedNode;
  try {
    node = expandObject(value, state, depth, sensitiveKeyPredicate);
  } catch {
    state.truncated = true;
    node = scalarNode(UNAVAILABLE_VALUE, state);
  } finally {
    state.ancestors.delete(value);
  }

  // A `[Circular]` marker is relative to the path that produced it, so a
  // subtree containing one is never cached.
  if (!node.cyclic) {
    const entries = state.memo.get(value) ?? new Map<string, MemoEntry>();
    entries.set(memoKey, { size: node.size, value: node.value });
    state.memo.set(value, entries);
  }
  return node;
};

const buildSafeAxiosDiagnostics = (
  error: AxiosError,
  state: SanitizationState,
  depth: number,
): SanitizedNode<SafeApiFailure> => {
  const config = error.config;
  const response = error.response;
  const method = scalarNode(getSafeHttpMethod(config?.method), state);
  const path = scalarNode(
    sanitizeUrl(config?.url, config?.baseURL, state),
    state,
  );
  const request: SafeLogRecord = {};
  const requestAccounting = createRecordAccounting(state);

  if (config?.headers !== undefined) {
    const headers = sanitizeValue(config.headers, state, depth + 1);
    request.headers = headers.value;
    accountRecordField("headers", headers, state, requestAccounting);
  }
  if (config?.params !== undefined) {
    const params = sanitizeValue(
      config.params,
      state,
      depth + 1,
      isSensitiveQueryKey,
    );
    request.params = params.value;
    accountRecordField("params", params, state, requestAccounting);
  }
  if (config?.data !== undefined) {
    const body = sanitizeValue(config.data, state, depth + 1);
    request.body = body.value;
    accountRecordField("body", body, state, requestAccounting);
  }
  if (
    isNumberValue(config?.timeout) &&
    Number.isSafeInteger(config.timeout) &&
    config.timeout >= 0
  ) {
    const timeout = scalarNode(config.timeout, state);
    request.timeout = timeout.value;
    accountRecordField("timeout", timeout, state, requestAccounting);
  }
  if (config?.auth !== undefined) {
    const auth = scalarNode(SAFE_CONSOLE_REDACTED, state);
    request.auth = auth.value;
    accountRecordField("auth", auth, state, requestAccounting);
  }

  const code = getSafeErrorCodeNode(error.code, state);
  const errorName = sanitizeStringNode(error.name || "AxiosError", state);
  const outcomeValue: SafeApiFailure["outcome"] = response ? "http" : "network";
  const outcome = scalarNode(outcomeValue, state);
  const responseStatus = response?.status;
  const status = scalarNode(
    isNumberValue(responseStatus) &&
      Number.isInteger(responseStatus) &&
      responseStatus >= 100 &&
      responseStatus <= 599
      ? responseStatus
      : null,
    state,
  );
  const requestNode: SanitizedNode<SafeLogRecord> = {
    cyclic: requestAccounting.cyclic,
    size: requestAccounting.size,
    value: request,
  };

  const safeFailure: SafeApiFailure = {
    code: code.value,
    errorName: errorName.value,
    method: method.value,
    outcome: outcome.value,
    path: path.value,
    request,
    status: status.value,
  };
  const failureAccounting = createRecordAccounting(state);
  accountRecordField("code", code, state, failureAccounting);
  accountRecordField("errorName", errorName, state, failureAccounting);
  accountRecordField("method", method, state, failureAccounting);
  accountRecordField("outcome", outcome, state, failureAccounting);
  accountRecordField("path", path, state, failureAccounting);
  accountRecordField("request", requestNode, state, failureAccounting);
  accountRecordField("status", status, state, failureAccounting);

  if (error.message) {
    const message = sanitizeStringNode(error.message, state);
    safeFailure.message = message.value;
    accountRecordField("message", message, state, failureAccounting);
  }
  if (error.stack) {
    const stack = sanitizeStringNode(error.stack, state);
    safeFailure.stack = stack.value;
    accountRecordField("stack", stack, state, failureAccounting);
  }

  if (response) {
    const safeResponse: SafeLogRecord = {};
    const responseAccounting = createRecordAccounting(state);
    if (response.statusText) {
      const statusText = sanitizeStringNode(response.statusText, state);
      safeResponse.statusText = statusText.value;
      accountRecordField("statusText", statusText, state, responseAccounting);
    }
    if (response.headers !== undefined) {
      const headers = sanitizeValue(response.headers, state, depth + 1);
      safeResponse.headers = headers.value;
      accountRecordField("headers", headers, state, responseAccounting);
    }
    if (response.data !== undefined) {
      const body = sanitizeValue(response.data, state, depth + 1);
      safeResponse.body = body.value;
      accountRecordField("body", body, state, responseAccounting);
    }
    safeFailure.response = safeResponse;
    accountRecordField(
      "response",
      {
        cyclic: responseAccounting.cyclic,
        size: responseAccounting.size,
        value: safeResponse,
      },
      state,
      failureAccounting,
    );
  }
  return {
    cyclic: failureAccounting.cyclic,
    size: failureAccounting.size,
    value: safeFailure,
  };
};

export const sanitizeLogContext = (context: unknown): SafeConsolePayload => {
  const state = createSanitizationState();
  try {
    return {
      data: sanitizeValue(context, state).value,
      truncated: state.truncated,
    };
  } catch {
    return { data: UNAVAILABLE_VALUE, truncated: true };
  }
};

/** The public sink accepts exactly the arbitrary input handled by its sanitizer. */
type SanitizableLogContext = Parameters<typeof sanitizeLogContext>[0];

/**
 * Shared transport-boundary string sanitizer for telemetry fields which must
 * remain strings, such as Sentry span descriptions.
 */
export const sanitizeDiagnosticString = (value: string): string => {
  const state = createSanitizationState();
  try {
    return sanitizeString(value, state);
  } catch {
    return UNAVAILABLE_VALUE;
  }
};

export const getSafeAxiosDiagnostics = (
  cause: unknown,
): SafeApiFailure | null => {
  try {
    if (!isAxiosError(cause)) return null;
    const state = createSanitizationState();
    const failure = buildSafeAxiosDiagnostics(cause, state, 0).value;
    // Surfaced on the failure itself because `reportApiError` re-sanitizes this
    // object through `safeConsole`, whose own payload flag only reflects that
    // second pass and would otherwise read `false` beside `[Truncated]` values.
    if (state.truncated) failure.truncated = true;
    return failure;
  } catch {
    return null;
  }
};

export const getSafeErrorDiagnostics = (cause: unknown): SafeLogValue => {
  const axiosDiagnostics = getSafeAxiosDiagnostics(cause);
  return axiosDiagnostics ?? sanitizeLogContext(cause).data;
};

const getSafeOperation = (operation: string): SafeConsoleOperation =>
  isStringValue(operation) &&
  Object.prototype.hasOwnProperty.call(SAFE_CONSOLE_OPERATIONS, operation)
    ? (operation as SafeConsoleOperation)
    : "unregistered-operation";

type SafeConsoleLevel = "error" | "log" | "warn";
type SafeBreadcrumb = Parameters<typeof Sentry.addBreadcrumb>[0];

type SafeConsoleSinks = {
  addBreadcrumb: (breadcrumb: SafeBreadcrumb) => void;
  console: Pick<Console, SafeConsoleLevel>;
};

export const createSafeConsole = ({
  addBreadcrumb,
  console: consoleSink,
}: SafeConsoleSinks) => {
  const emit = (
    level: SafeConsoleLevel,
    operation: SafeConsoleOperation,
    context: readonly SanitizableLogContext[],
  ): void => {
    const safeOperation = getSafeOperation(operation);
    const message = `${SAFE_CONSOLE_MARKER} ${safeOperation} ${SAFE_CONSOLE_OPERATIONS[safeOperation]}`;
    const payload =
      context.length === 0
        ? undefined
        : sanitizeLogContext(context.length === 1 ? context[0] : context);

    try {
      if (payload === undefined) {
        consoleSink[level](message);
      } else {
        consoleSink[level](message, payload);
      }
    } catch {
      // A failed local sink must not prevent the independent Sentry breadcrumb.
    }

    try {
      const breadcrumb: SafeBreadcrumb = {
        category: "console",
        level: level === "warn" ? "warning" : level,
        message,
      };
      if (payload !== undefined) {
        // Sentry normalizes breadcrumb objects at depth 3. Encode only the
        // already-sanitized value so nested request/response evidence survives.
        breadcrumb.data = {
          context: JSON.stringify(payload.data),
          truncated: payload.truncated,
        };
      }
      addBreadcrumb(breadcrumb);
    } catch {
      // Diagnostic logging must never interrupt application flow.
    }
  };

  return {
    error: (
      operation: SafeConsoleOperation,
      ...context: SanitizableLogContext[]
    ): void => emit("error", operation, context),
    log: (
      operation: SafeConsoleOperation,
      ...context: SanitizableLogContext[]
    ): void => emit("log", operation, context),
    warn: (
      operation: SafeConsoleOperation,
      ...context: SanitizableLogContext[]
    ): void => emit("warn", operation, context),
  };
};

export const safeConsole = createSafeConsole({
  addBreadcrumb: Sentry.addBreadcrumb,
  console: globalThis.console,
});
